import { addDays, addMonths, diffDays, type ISODate } from './dates';

/**
 * Announcements of the start page (company-wide notices): who sees them, when they are active, how they repeat.
 * Pure rules; storage and permissions are on the server.
 *
 * Types (what the start page and the filters distinguish):
 * info    general information
 * event   company event or appointment (summer party, team meeting): also listed under "Demnächst"
 * closure company holiday or bridge day (office closed): also under "Demnächst"
 * deadline reminder with a date (hours submitted, holiday planning, month end): also under "Demnächst"
 * safety  occupational safety and instructions: confirmation of reading by default (proof for the instruction)
 * it      IT and system notices (maintenance, password, new function)
 * policy  guideline or document changed: link to the page where the company keeps it
 */
export const ANNOUNCEMENT_TYPES = ['info', 'event', 'closure', 'deadline', 'safety', 'it', 'policy'] as const;
export type AnnouncementType = (typeof ANNOUNCEMENT_TYPES)[number];

export const ANNOUNCEMENT_PRIORITIES = ['normal', 'important', 'critical'] as const;
export type AnnouncementPriority = (typeof ANNOUNCEMENT_PRIORITIES)[number];

/** Types that have a date and appear in the list of coming dates. */
export const DATED_TYPES: readonly AnnouncementType[] = ['event', 'closure', 'deadline'];

/** Defaults when a type is chosen in the form (the publisher can change them). */
export function typeDefaults(type: AnnouncementType): { priority: AnnouncementPriority; requiresAck: boolean; dated: boolean } {
  switch (type) {
    case 'safety':
      return { priority: 'important', requiresAck: true, dated: false };
    case 'closure':
      return { priority: 'important', requiresAck: false, dated: true };
    case 'deadline':
      return { priority: 'normal', requiresAck: false, dated: true };
    case 'event':
      return { priority: 'normal', requiresAck: false, dated: true };
    default:
      return { priority: 'normal', requiresAck: false, dated: false };
  }
}

/**
 * Audience: everyone (all internal employees) or the union of the criteria. Locations are holiday calendars of the
 * person (DE-BE, TR), roles are the roles of the app plus "lead" (project lead), projects are current project teams.
 * External people only see announcements aimed at their role "external" or at one of their projects.
 */
export interface Audience {
  all?: boolean;
  locations?: string[];
  roles?: string[];
  projectIds?: string[];
}

export interface AudienceViewer {
  location: string;
  role: string;
  kind: 'employee' | 'external';
  isLead: boolean;
  projectIds: ReadonlySet<string>;
}

export function audienceIsEmpty(a: Audience): boolean {
  return !a.all && !(a.locations?.length || a.roles?.length || a.projectIds?.length);
}

/** Role names of announcements written before the office and the management became one role. */
const ROLE_ALIASES: Record<string, string> = { management: 'office' };

export function audienceMatches(a: Audience, v: AudienceViewer): boolean {
  if (a.all) return v.kind === 'employee';
  if (a.locations?.includes(v.location)) return true;
  if (a.roles?.some((r) => (ROLE_ALIASES[r] ?? r) === v.role || (r === 'lead' && v.isLead))) return true;
  if (a.projectIds?.some((p) => v.projectIds.has(p))) return true;
  return false;
}

/** freq: weekly (every n weeks), monthly (every n months, same day), yearly. durationDays: how long one occurrence stays; leadDays: shown that many days before. */
export interface Recurrence {
  freq: 'weekly' | 'monthly' | 'yearly';
  interval: number;
  until?: ISODate | null;
  durationDays?: number;
  leadDays?: number;
}

export interface Schedule {
  startsOn: ISODate;
  endsOn?: ISODate | null;
  recurrence?: Recurrence | null;
}

function occurrenceStart(s: Schedule, k: number): ISODate {
  const r = s.recurrence!;
  const n = Math.max(1, r.interval || 1);
  if (r.freq === 'weekly') return addDays(s.startsOn, 7 * n * k);
  return addMonths(s.startsOn, (r.freq === 'yearly' ? 12 : 1) * n * k);
}

/** First occurrence whose end is not before `date` (k >= 0), found by stepping; series are short (weekly for years = a few hundred steps). */
function occurrenceAtOrAfter(s: Schedule, date: ISODate): { start: ISODate; k: number } | null {
  const r = s.recurrence;
  if (!r) return null;
  const duration = Math.max(1, r.durationDays ?? 1);
  let k = 0;
  if (r.freq === 'weekly') {
    const step = 7 * Math.max(1, r.interval || 1);
    k = Math.max(0, Math.floor((diffDays(date, s.startsOn) - (duration - 1)) / step));
  }
  for (let guard = 0; guard < 5000; guard++, k++) {
    const start = occurrenceStart(s, k);
    if (r.until && start > r.until) return null;
    if (addDays(start, duration - 1) >= date) return { start, k };
  }
  return null;
}

/**
 * Start date of the occurrence that is visible on `date` (inside [start - lead, start + duration - 1]), or null.
 * A single announcement is its own occurrence: [startsOn, endsOn], open-ended without endsOn.
 */
export function visibleOccurrence(s: Schedule, date: ISODate): ISODate | null {
  if (!s.recurrence) {
    if (date < s.startsOn) return null;
    if (s.endsOn && date > s.endsOn) return null;
    return s.startsOn;
  }
  const occ = occurrenceAtOrAfter(s, date);
  if (!occ) return null;
  const lead = Math.max(0, s.recurrence.leadDays ?? 0);
  return addDays(occ.start, -lead) <= date ? occ.start : null;
}

/** Next date (today or later) on which the thing happens: the event date, or the start of the next occurrence. */
export function nextDate(s: Schedule & { eventDate?: ISODate | null }, today: ISODate): ISODate | null {
  if (!s.recurrence) {
    const d = s.eventDate ?? s.startsOn;
    return d >= today ? d : null;
  }
  const occ = occurrenceAtOrAfter({ ...s, startsOn: s.startsOn }, today);
  if (!occ) return null;
  // an occurrence in progress counts for today
  return occ.start < today ? today : occ.start;
}

/** Order on the start page: critical first, then pinned, then important, then the newest. */
export function compareAnnouncements(
  a: { priority: AnnouncementPriority; pinned: boolean; startsOn: ISODate },
  b: { priority: AnnouncementPriority; pinned: boolean; startsOn: ISODate },
): number {
  const rank = (x: typeof a) => (x.priority === 'critical' ? 0 : x.pinned ? 1 : x.priority === 'important' ? 2 : 3);
  return rank(a) - rank(b) || b.startsOn.localeCompare(a.startsOn);
}

export function parseRecurrence(raw: unknown): Recurrence | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Partial<Recurrence>;
  if (r.freq !== 'weekly' && r.freq !== 'monthly' && r.freq !== 'yearly') return null;
  return {
    freq: r.freq,
    interval: Math.min(52, Math.max(1, Math.round(r.interval ?? 1))),
    until: r.until ?? null,
    durationDays: Math.min(60, Math.max(1, Math.round(r.durationDays ?? 1))),
    leadDays: Math.min(60, Math.max(0, Math.round(r.leadDays ?? 0))),
  };
}
